import { MapPin, Phone, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import { SITE_LINKS } from "@/content/ContentContext";
import { useLanguage } from "@/i18n/LanguageContext";
import { PRODUCTS, INDUSTRIES } from "@/data/content";

const Footer = () => {
  const { t, pick } = useLanguage();
  const year = new Date().getFullYear();

  const companyHrefs = ["/why-us", "/contact", "/contact", "/contact"];
  const companyTestIds = ["why-us", "contact", "partners", "careers"];

  return (
    <footer data-testid="main-footer" className="border-t border-slate-800/80 bg-[#05070B]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-10">
          <div className="col-span-2">
            <div className="mb-4">
              <img src="/logo-full.png" alt="OTOSUBS — Autonomous Business Technology" className="h-14 w-auto" />
            </div>
            <p className="text-sm text-slate-400 leading-relaxed mb-6">{t("tagline")}</p>
            <address data-testid="footer-address" className="not-italic text-xs text-slate-500 leading-relaxed space-y-2 mb-6">
              <p className="text-slate-400 font-semibold">{SITE_LINKS.company}</p>
              <p className="flex items-start gap-2"><MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0 text-slate-500" />{SITE_LINKS.address}</p>
              <p className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 shrink-0 text-slate-500" /><a href={`tel:${(SITE_LINKS.phone || "").replace(/[^+\d]/g, "")}`} data-testid="footer-phone" className="hover:text-slate-300 transition-colors">{SITE_LINKS.phone}</a></p>
              <p className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 shrink-0 text-slate-500" /><a href={`mailto:${SITE_LINKS.email}`} data-testid="footer-email" className="hover:text-slate-300 transition-colors">{SITE_LINKS.email}</a></p>
            </address>
            <p className="text-[11px] font-mono2 uppercase tracking-[0.2em] text-indigo-400/80">{t("footer.promise")}</p>
          </div>

          <div>
            <h4 className="text-xs font-mono2 uppercase tracking-[0.2em] text-slate-500 mb-4">{t("footer.products")}</h4>
            <ul className="space-y-2.5">
              {PRODUCTS.map((p) => (
                <li key={p.slug}>
                  <Link data-testid={`footer-product-${p.slug}`} to={`/products/${p.slug}`} className="text-sm text-slate-400 hover:text-white transition-colors">
                    {p.short}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-xs font-mono2 uppercase tracking-[0.2em] text-slate-500 mb-4">{t("footer.industries")}</h4>
            <ul className="space-y-2.5">
              {INDUSTRIES.slice(0, 7).map((i) => (
                <li key={i.slug}>
                  <Link data-testid={`footer-industry-${i.slug}`} to={`/industries/${i.slug}`} className="text-sm text-slate-400 hover:text-white transition-colors">
                    {pick(i.name)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-xs font-mono2 uppercase tracking-[0.2em] text-slate-500 mb-4">{t("footer.company")}</h4>
            <ul className="space-y-2.5">
              {t("footer.companyLinks").map((label, idx) => (
                <li key={label}>
                  <Link data-testid={`footer-company-${companyTestIds[idx]}`} to={companyHrefs[idx]} className="text-sm text-slate-400 hover:text-white transition-colors">
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-xs font-mono2 uppercase tracking-[0.2em] text-slate-500 mb-4">{t("footer.resources")}</h4>
            <ul className="space-y-2.5 mb-8">
              {t("footer.resourceLinks").map((label) => (
                <li key={label}>
                  <span data-testid={`footer-resource-${label.toLowerCase().replace(/\s+/g, "-")}`} className="text-sm text-slate-500 cursor-default">{label}</span>
                </li>
              ))}
            </ul>
            <h4 className="text-xs font-mono2 uppercase tracking-[0.2em] text-slate-500 mb-4">{t("footer.legal")}</h4>
            <ul className="space-y-2.5">
              {t("footer.legalLinks").map((label) => (
                <li key={label}>
                  <span data-testid={`footer-legal-${label.toLowerCase().replace(/\s+/g, "-")}`} className="text-sm text-slate-500 cursor-default">{label}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-slate-800/60 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-slate-500">© {year} OTOSUBS — Autonomous Business Technology. {t("footer.rights")}</p>
          <p className="text-xs text-slate-600 font-mono2">Ampang, Selangor • Malaysia</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
